import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Receipt, Plus, X, Printer, PencilSimple, PaperPlaneTilt, FilePdf, Paperclip, CheckCircle, Trash,
} from "@phosphor-icons/react";
import StockPartCombobox from "@/components/StockPartCombobox";
import { fmtDate } from "@/lib/dates";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const blankPart = () => ({
  name: "", quantity: 1, stock_code: null, group: null, uom: null,
  unit_price: "", notes: "",
});

const STATUS_BADGE = {
  active:    "bg-emerald-700 text-white",
  expired:   "bg-amber-100 text-amber-900",
  cancelled: "bg-red-100 text-red-800 line-through",
};

export default function QuotationsPage() {
  const { user } = useAuth();
  // Manager creates + views; Finance views only.
  const canCreate = user.role === "manager";
  const canEmail  = user.role === "manager";

  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [invoicing, setInvoicing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus !== "all") params.status = filterStatus;
      if (search.trim()) params.q = search.trim();
      const [q, c] = await Promise.all([
        api.get("/quotations", { params }),
        api.get("/customers", { params: { active_only: true } }),
      ]);
      setRows(q.data || []);
      setCustomers(c.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterStatus]);
  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id); /* eslint-disable-next-line */ }, [search]);

  if (!["manager", "supervisor", "admin"].includes(user.role)) {
    return (
      <div className="p-10">
        <div className="text-sm text-neutral-500 border border-dashed border-neutral-300 p-8 text-center">
          You don't have access to Quotations.
        </div>
      </div>
    );
  }

  const openPdf = (q) => window.open(`${API_BASE}/quotations/${q.id}/receipt.pdf`, "_blank", "noopener");

  const cancelQt = async (q) => {
    const reason = window.prompt(`Cancel ${q.qt_no}? (Optional reason)`, "");
    if (reason === null) return;
    try {
      await api.post(`/quotations/${q.id}/cancel`, null, { params: reason ? { reason } : {} });
      toast.success(`${q.qt_no} cancelled`); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const emailQt = async (q) => {
    if (!window.confirm(`Email ${q.qt_no} to ${q.customer_email || "customer"}? (You will be CC'd)`)) return;
    try {
      const res = await api.post(`/quotations/${q.id}/email`);
      toast.success(`Sent to ${res.data.to}${res.data.cc?.length ? ` · CC ${res.data.cc.join(", ")}` : ""}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <div className="border-b border-neutral-200 pt-10">
        <div className="flex items-start justify-between px-10 pb-6">
          <div>
            <div className="label-eyebrow">Sales</div>
            <h1 className="font-display text-4xl tracking-tight leading-none mt-2 flex items-center gap-3">
              <Receipt size={36} weight="duotone" className="text-purple-700" /> Quotations
            </h1>
            <p className="text-sm text-neutral-500 mt-2 max-w-lg">
              Numbered <span className="font-mono">QT-10001+</span>. Manager-only creation with a validity window (default 30 days). Supervisor can view. No stock movement.
            </p>
          </div>
          {canCreate && (
            <Button onClick={() => setShowCreate(true)} data-testid="new-qt-btn"
              className="rounded-none bg-neutral-900 text-white hover:bg-neutral-800">
              <Plus size={14} weight="bold" className="mr-1" /> New Quotation
            </Button>
          )}
        </div>
        <div className="px-10 pb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <Input placeholder="Search quote no or customer…" value={search} onChange={(e) => setSearch(e.target.value)}
            className="h-10 rounded-none border-neutral-300" data-testid="qt-search" />
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger data-testid="qt-status-filter" className="h-10 rounded-none border-neutral-300"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="px-10 py-6">
        {loading ? (
          <div className="text-sm text-neutral-500 font-mono">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-neutral-500 border border-dashed border-neutral-300 p-8 text-center">
            No quotations yet.
          </div>
        ) : (
          <div className="border border-neutral-300 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 border-b border-neutral-300">
                <tr>
                  <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-wider">QT No</th>
                  <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-wider">Customer</th>
                  <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-wider">Lines</th>
                  <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-wider">Total (RM)</th>
                  <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-wider">Invoice</th>
                  <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-wider">Valid until</th>
                  <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-wider">Status</th>
                  <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-wider">Prepared by</th>
                  <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((q) => {
                  const total = (q.parts || []).reduce((s, p) => s + (Number(p.unit_price) || 0) * (Number(p.quantity) || 0), 0);
                  return (
                    <tr key={q.id} className="border-b border-neutral-100 last:border-0" data-testid={`qt-row-${q.qt_no}`}>
                      <td className="px-3 py-2 font-mono font-semibold">{q.qt_no}</td>
                      <td className="px-3 py-2">{q.customer_name}</td>
                      <td className="px-3 py-2 text-right font-mono">{q.parts?.length || 0}</td>
                      <td className="px-3 py-2 text-right font-mono">{total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td className="px-3 py-2">
                        {q.invoice ? (
                          <button onClick={() => setInvoicing(q)} data-testid={`qt-invoice-${q.qt_no}`}
                            className="text-left group">
                            <div className="text-[11px] font-mono font-semibold group-hover:underline">{q.invoice.iv_no}</div>
                            <div className="flex items-center gap-1 mt-0.5">
                              <span className={`px-1 py-0.5 text-[9px] font-mono uppercase tracking-wider ${
                                q.invoice.status === "approved" ? "bg-emerald-700 text-white"
                                  : q.invoice.status === "cancelled" ? "bg-red-100 text-red-800"
                                  : "bg-amber-100 text-amber-900"
                              }`}>{q.invoice.status}</span>
                              {q.invoice.attachment && <Paperclip size={10} weight="bold" className="text-neutral-600" />}
                              {q.invoice.do_no && <span className="text-[9px] font-mono text-emerald-800">→ {q.invoice.do_no}</span>}
                            </div>
                          </button>
                        ) : (
                          canCreate && q.status !== "cancelled" ? (
                            <button onClick={() => setInvoicing(q)}
                              className="h-6 px-2 text-[10px] font-mono uppercase tracking-wider border border-neutral-400 text-neutral-700 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white"
                              data-testid={`qt-invoice-new-${q.qt_no}`}>
                              + Invoice
                            </button>
                          ) : <span className="text-neutral-300 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-[11px] font-mono">{q.expires_at ? fmtDate(q.expires_at) : "—"}<div className="text-[10px] text-neutral-500">{q.validity_days}d</div></td>
                      <td className="px-3 py-2"><span className={`px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider ${STATUS_BADGE[q.status] || ""}`}>{q.status}</span></td>
                      <td className="px-3 py-2 text-xs">{q.prepared_by_name || q.created_by_name}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => openPdf(q)} title="Print / PDF" className="h-7 w-7 flex items-center justify-center border border-neutral-300 hover:border-neutral-900" data-testid={`qt-print-${q.qt_no}`}><Printer size={13} weight="bold" /></button>
                          {canEmail && q.customer_email && (
                            <button onClick={() => emailQt(q)} title={`Email to ${q.customer_email}`} className="h-7 w-7 flex items-center justify-center border border-neutral-300 hover:border-emerald-700 hover:text-emerald-800" data-testid={`qt-email-${q.qt_no}`}><PaperPlaneTilt size={13} weight="bold" /></button>
                          )}
                          {canCreate && q.status !== "cancelled" && q.invoice?.status !== "approved" && (
                            <button onClick={() => setEditing(q)} title="Edit" className="h-7 w-7 flex items-center justify-center border border-neutral-300 hover:border-neutral-900" data-testid={`qt-edit-${q.qt_no}`}><PencilSimple size={13} weight="bold" /></button>
                          )}
                          {canCreate && q.status !== "cancelled" && (
                            <button onClick={() => cancelQt(q)} title="Cancel" className="h-7 w-7 flex items-center justify-center border border-neutral-300 hover:border-red-600 hover:text-red-600" data-testid={`qt-cancel-${q.qt_no}`}><X size={13} weight="bold" /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(showCreate || editing) && (
        <QTEditDialog
          editing={editing}
          customers={customers}
          onClose={() => { setShowCreate(false); setEditing(null); }}
          onSaved={() => { setShowCreate(false); setEditing(null); load(); }}
        />
      )}
      {invoicing && (
        <QuotationInvoiceDialog
          quote={invoicing}
          canApprove={user.role === "manager"}
          onClose={() => setInvoicing(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

function QTEditDialog({ editing, customers, onClose, onSaved }) {
  const isEdit = !!editing;
  const [customerId, setCustomerId] = useState(editing?.customer_id || "");
  const [validityDays, setValidityDays] = useState(editing?.validity_days ?? 30);
  const [preparedByName, setPreparedByName] = useState(editing?.prepared_by_name || "");
  const [terms, setTerms] = useState(editing?.terms || "Prices are subject to change without notice.\n50% deposit required to confirm order.");
  const [notes, setNotes] = useState(editing?.notes || "");
  const [items, setItems] = useState(() =>
    (editing?.parts?.length ? editing.parts : [blankPart()]).map((p) => ({
      name: p.name || "", quantity: p.quantity || 1,
      stock_code: p.stock_code || null, group: p.group || null, uom: p.uom || null,
      unit_price: p.unit_price ?? "", notes: p.notes || "",
    })),
  );
  const [busy, setBusy] = useState(false);

  const setItem = (i, key, val) => { const n = items.slice(); n[i] = { ...n[i], [key]: val }; setItems(n); };
  const pickItem = (i, picked) => setItems(items.map((row, idx) => (idx === i ? {
    ...row, name: picked.name || "", stock_code: picked.stock_code || null,
    group: picked.group || null, uom: picked.uom || null,
    unit_price: row.unit_price || picked.unit_cost || "",
  } : row)));
  const addRow = () => setItems([...items, blankPart()]);
  const removeRow = (i) => setItems(items.length > 1 ? items.filter((_, idx) => idx !== i) : items);

  const total = items.reduce((s, p) => s + (Number(p.unit_price) || 0) * (Number(p.quantity) || 0), 0);

  const submit = async () => {
    if (!customerId) return toast.error("Pick a customer");
    const clean = items.filter((p) => (p.name || "").trim() && Number(p.quantity) > 0);
    if (!clean.length) return toast.error("Add at least one line");
    setBusy(true);
    try {
      const payload = {
        customer_id: customerId,
        validity_days: Math.max(1, Number(validityDays) || 30),
        prepared_by_name: preparedByName || null,
        terms, notes,
        parts: clean.map((p) => ({
          name: p.name.trim(),
          quantity: Math.max(1, Number(p.quantity) || 1),
          stock_code: p.stock_code || null,
          group: p.group || null, uom: p.uom || null,
          unit_price: p.unit_price === "" ? null : Number(p.unit_price),
          notes: p.notes || "",
        })),
      };
      if (isEdit) await api.patch(`/quotations/${editing.id}`, payload);
      else await api.post("/quotations", payload);
      toast.success(isEdit ? "Quotation updated" : "Quotation created");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl rounded-none border-neutral-900 max-h-[92vh] overflow-y-auto" data-testid="qt-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">
            {isEdit ? `Edit ${editing.qt_no}` : "New Quotation"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label className="label-eyebrow">Customer *</Label>
              <Select value={customerId} onValueChange={setCustomerId}>
                <SelectTrigger data-testid="qt-customer" className="h-10 rounded-none border-neutral-400"><SelectValue placeholder="Pick customer" /></SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      <span className="flex items-center gap-2">
                        {c.name}
                        {c.customer_type === "walkin" && (
                          <span className="text-[9px] font-mono uppercase tracking-wider bg-amber-100 text-amber-900 px-1 py-0.5">
                            walk-in
                          </span>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Validity (days) *</Label>
              <Input type="number" min={1} value={validityDays} onChange={(e) => setValidityDays(e.target.value)}
                className="h-10 rounded-none border-neutral-400 font-mono" data-testid="qt-validity" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="label-eyebrow">Prepared by <span className="text-neutral-400 normal-case">(optional override)</span></Label>
            <Input value={preparedByName} onChange={(e) => setPreparedByName(e.target.value)}
              placeholder="Falls back to your name" className="h-10 rounded-none border-neutral-400" data-testid="qt-prepared-by" />
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label className="label-eyebrow">Lines *</Label>
              <Button onClick={addRow} size="sm" variant="outline" className="h-7 rounded-none border-neutral-400" data-testid="qt-add-line">
                <Plus size={12} weight="bold" className="mr-1" /> Add line
              </Button>
            </div>
            {items.map((p, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`qt-row-${i}`}>
                <div className="col-span-7"><StockPartCombobox value={p} onChange={(pk) => pickItem(i, pk)} testid={`qt-picker-${i}`} /></div>
                <Input type="number" min={1} value={p.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)}
                  className="col-span-2 h-9 rounded-none border-neutral-400 text-right font-mono" data-testid={`qt-qty-${i}`} placeholder="Qty" />
                <Input type="number" step="0.01" value={p.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)}
                  className="col-span-2 h-9 rounded-none border-neutral-400 text-right font-mono" data-testid={`qt-price-${i}`} placeholder="Unit price" />
                <button type="button" onClick={() => removeRow(i)} disabled={items.length === 1}
                  className="col-span-1 h-9 flex items-center justify-center border border-neutral-300 hover:border-red-600 hover:text-red-600 disabled:opacity-40" aria-label="Remove"><X size={13} weight="bold" /></button>
              </div>
            ))}
          </div>

          <div className="flex justify-between items-center text-sm border-t border-neutral-200 pt-2 font-mono">
            <span className="text-neutral-500 label-eyebrow">Grand Total</span>
            <span className="font-semibold">RM {total.toFixed(2)}</span>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Terms &amp; Conditions</Label>
              <textarea value={terms} onChange={(e) => setTerms(e.target.value)} rows={4}
                className="w-full rounded-none border border-neutral-400 px-2 py-1.5 text-sm focus:outline-none focus:border-neutral-900" data-testid="qt-terms" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Internal Notes</Label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4}
                className="w-full rounded-none border border-neutral-400 px-2 py-1.5 text-sm focus:outline-none focus:border-neutral-900" data-testid="qt-notes" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none border-neutral-900">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="qt-save" className="rounded-none bg-neutral-900 text-white hover:bg-neutral-800">
            {busy ? "Saving…" : (isEdit ? "Save changes" : "Create quotation")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -------------------------------------------------------------------------
// Quotation → Invoice → auto-DO flow
// -------------------------------------------------------------------------
function QuotationInvoiceDialog({ quote, canApprove, onClose, onChanged }) {
  const [q, setQ] = useState(quote);
  const inv = q.invoice || null;
  const [subtotal, setSubtotal] = useState(
    inv?.subtotal ??
      (q.parts || []).reduce((s, p) => s + (Number(p.unit_price) || 0) * (Number(p.quantity) || 0), 0),
  );
  const [tax, setTax] = useState(inv?.tax_amount ?? 0);
  const [notes, setNotes] = useState(inv?.notes || "");
  const [busy, setBusy] = useState(false);
  // Preview state for inline PDF/image display.
  const [preview, setPreview] = useState(null);
  const fileRef = React.useRef(null);

  useEffect(() => {
    return () => { if (preview?.url) URL.revokeObjectURL(preview.url); };
  }, [preview]);

  const total = (Number(subtotal) || 0) + (Number(tax) || 0);
  const isDraft = inv?.status === "draft";
  const isApproved = inv?.status === "approved";

  const refresh = async () => {
    try {
      const r = await api.get(`/quotations/${quote.id}`);
      setQ(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const createInvoice = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/quotations/${quote.id}/invoice`, {
        subtotal: Number(subtotal),
        tax_amount: Number(tax) || 0,
        notes,
      });
      toast.success(`Invoice ${r.data.invoice.iv_no} created`);
      setQ(r.data);
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const updateInvoice = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/quotations/${quote.id}/invoice`, {
        subtotal: Number(subtotal),
        tax_amount: Number(tax) || 0,
        notes,
      });
      toast.success("Invoice updated");
      setQ(r.data);
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const uploadAttachment = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("File too large — 15 MB max");
      e.target.value = "";
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/quotations/${quote.id}/invoice/attachment`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Attached ${file.name}`);
      await refresh();
      onChanged && onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
    finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const previewAttachment = async () => {
    try {
      const res = await api.get(`/quotations/${quote.id}/invoice/attachment`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const ct = (inv.attachment.content_type || res.data.type || "").toLowerCase();
      const kind = ct.includes("pdf") ? "pdf" : ct.startsWith("image/") ? "image" : "other";
      if (kind === "other") {
        window.open(url, "_blank", "noopener");
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
        return;
      }
      setPreview({ url, kind, filename: inv.attachment.original_filename });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const closePreview = () => { if (preview?.url) URL.revokeObjectURL(preview.url); setPreview(null); };

  const removeAttachment = async () => {
    if (!window.confirm("Remove the attachment?")) return;
    try {
      await api.delete(`/quotations/${quote.id}/invoice/attachment`);
      toast.success("Attachment removed");
      if (preview) closePreview();
      await refresh();
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const approve = async () => {
    if (!window.confirm(`Approve invoice ${inv.iv_no}? This will auto-generate a DRAFT Delivery Order.`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/quotations/${quote.id}/invoice/approve`);
      toast.success(`Approved · draft ${r.data.invoice.do_no} created`);
      setQ(r.data);
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const cancelInvoice = async () => {
    if (!window.confirm("Cancel this invoice?")) return;
    setBusy(true);
    try {
      const r = await api.post(`/quotations/${quote.id}/invoice/cancel`);
      toast.success("Invoice cancelled");
      setQ(r.data);
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className={`${preview ? "max-w-5xl" : "max-w-2xl"} rounded-none border-neutral-900 max-h-[92vh] overflow-hidden flex flex-col`} data-testid="qt-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2 flex-wrap">
            {preview ? (
              <button onClick={closePreview}
                className="h-7 px-2 text-[10px] font-mono uppercase tracking-wider border border-neutral-900 hover:bg-neutral-900 hover:text-white"
                data-testid="qt-invoice-preview-back">← Back</button>
            ) : null}
            Invoice · {quote.qt_no}
            {inv && (
              <>
                <span className="font-mono text-lg text-neutral-500">/ {inv.iv_no}</span>
                <span className={`px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider ${
                  inv.status === "approved" ? "bg-emerald-700 text-white"
                    : inv.status === "cancelled" ? "bg-red-100 text-red-800 line-through"
                    : "bg-amber-100 text-amber-900"
                }`}>{inv.status}</span>
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        {preview ? (
          <div className="flex-1 min-h-[60vh] border border-neutral-300 bg-neutral-50 overflow-auto">
            {preview.kind === "pdf" ? (
              <iframe src={preview.url} title={preview.filename}
                className="w-full h-full min-h-[60vh]" data-testid="qt-invoice-preview-pdf" />
            ) : (
              <div className="w-full h-full flex items-center justify-center p-4">
                <img src={preview.url} alt={preview.filename}
                  className="max-w-full max-h-[70vh] object-contain" data-testid="qt-invoice-preview-img" />
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4 overflow-y-auto">
            <div className="text-sm text-neutral-600">
              Bill this quote. On approval, a <span className="font-mono">DRAFT Delivery Order</span> is auto-created carrying the quote's parts.
              Storekeeper still approves + releases through the usual flow — stock deducts on release.
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="label-eyebrow">Subtotal (RM) *</Label>
                <Input type="number" step="0.01" value={subtotal}
                  onChange={(e) => setSubtotal(e.target.value)} disabled={!isDraft && inv}
                  className="h-10 rounded-none border-neutral-400 font-mono text-right" data-testid="qt-invoice-subtotal" />
              </div>
              <div className="space-y-1.5">
                <Label className="label-eyebrow">Tax (RM)</Label>
                <Input type="number" step="0.01" value={tax}
                  onChange={(e) => setTax(e.target.value)} disabled={!isDraft && inv}
                  className="h-10 rounded-none border-neutral-400 font-mono text-right" data-testid="qt-invoice-tax" />
              </div>
              <div className="space-y-1.5">
                <Label className="label-eyebrow">Total (RM)</Label>
                <div className="h-10 flex items-center justify-end px-3 border border-neutral-300 bg-neutral-50 font-mono font-semibold" data-testid="qt-invoice-total">
                  {(Number(total) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="label-eyebrow">Notes</Label>
              <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} disabled={!isDraft && inv}
                className="w-full rounded-none border border-neutral-400 px-2 py-1.5 text-sm focus:outline-none focus:border-neutral-900" data-testid="qt-invoice-notes" />
            </div>

            {inv && (
              <div className="border border-neutral-200 p-3 space-y-2" data-testid="qt-invoice-attachment-block">
                <div className="flex items-center justify-between">
                  <div className="label-eyebrow">Attachment (optional PDF or photo)</div>
                  {inv.attachment && (
                    <span className="text-[10px] font-mono text-neutral-500">
                      {inv.attachment.original_filename} · {fmtDate(inv.attachment.uploaded_at)}
                    </span>
                  )}
                </div>
                {inv.attachment ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm">
                      <FilePdf size={16} weight="duotone" className="text-red-700" />
                      <span>{inv.attachment.original_filename}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={previewAttachment}
                        className="h-7 px-2 text-[10px] font-mono uppercase tracking-wider border border-neutral-900 hover:bg-neutral-900 hover:text-white"
                        data-testid="qt-invoice-attach-preview">Preview</button>
                      {isDraft && (
                        <button onClick={removeAttachment}
                          className="h-7 w-7 flex items-center justify-center border border-neutral-300 hover:border-red-600 hover:text-red-600"
                          data-testid="qt-invoice-attach-del"><Trash size={12} weight="bold" /></button>
                      )}
                    </div>
                  </div>
                ) : isDraft ? (
                  <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif"
                    onChange={uploadAttachment} disabled={busy} data-testid="qt-invoice-attach-input"
                    className="text-sm" />
                ) : (
                  <div className="text-xs text-neutral-500">No attachment.</div>
                )}
              </div>
            )}

            {isApproved && inv?.do_no && (
              <div className="border border-emerald-700 bg-emerald-50 p-3 flex items-center justify-between" data-testid="qt-invoice-do-link">
                <div className="text-sm">
                  <CheckCircle size={16} weight="duotone" className="inline-block mr-1 text-emerald-700" />
                  Auto-generated draft <span className="font-mono font-semibold">{inv.do_no}</span> ready for Manager approval → Storekeeper release.
                </div>
                <a href={`/delivery-orders?status=draft#${inv.do_no}`}
                  className="h-8 px-3 flex items-center border border-emerald-700 text-emerald-800 hover:bg-emerald-700 hover:text-white text-[10px] font-mono uppercase tracking-wider">
                  Open DO →
                </a>
              </div>
            )}
          </div>
        )}

        {!preview && (
          <DialogFooter>
            {!inv && (
              <Button onClick={createInvoice} disabled={busy} data-testid="qt-invoice-create"
                className="rounded-none bg-neutral-900 text-white hover:bg-neutral-800">
                {busy ? "Creating…" : "Create Invoice"}
              </Button>
            )}
            {inv && isDraft && (
              <>
                <Button onClick={updateInvoice} disabled={busy} variant="outline"
                  data-testid="qt-invoice-update" className="rounded-none border-neutral-900">
                  Save changes
                </Button>
                {canApprove && (
                  <Button onClick={approve} disabled={busy} data-testid="qt-invoice-approve"
                    className="rounded-none bg-emerald-700 text-white hover:bg-emerald-800">
                    <CheckCircle size={14} weight="bold" className="mr-1" />
                    Approve → Create DO
                  </Button>
                )}
                {canApprove && (
                  <Button onClick={cancelInvoice} disabled={busy} variant="outline"
                    data-testid="qt-invoice-cancel" className="rounded-none border-red-600 text-red-700">
                    <X size={14} weight="bold" className="mr-1" /> Cancel Invoice
                  </Button>
                )}
              </>
            )}
            <Button variant="outline" onClick={onClose} className="rounded-none border-neutral-900">Close</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
